import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ChangeDetectionStrategy
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { DateTime } from 'luxon';
import { IdGeneratorPipe } from '../../pipes/id-generator.pipe';
import { DateTimeService } from '../../services/date-time.service';
import { IconSanitizerService } from '../../services/icon-sanitizer.service';
import { CDK_IMPORTS } from '../../shared/standalone-imports';
import {
    DateRanges,
    EventsForPeriod,
    FullViewCalendarViewType,
    IFullViewCalendarEvent
} from '../../types/full-view-calendar.type';
import { CalendarType } from '../calendar/calendar.component';
import { DatepickerDropdownComponent } from '../datepicker-dropdown/datepicker-dropdown.component';
import { CalendarViewComponent } from './calendar-view/calendar-view.component';

@Component({
    selector: 'lib-full-view-calendar',
    templateUrl: './full-view-calendar.component.html',
    styleUrls: ['./full-view-calendar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, IdGeneratorPipe, ...CDK_IMPORTS, DatepickerDropdownComponent, CalendarViewComponent]
})
export class FullViewCalendarComponent<T> implements OnChanges {
    @Input()
    showViewSwitcher: boolean;

    @Input()
    events?: IFullViewCalendarEvent<T>[];

    @Input()
    eventTemplate!: TemplateRef<any>;

    @Output()
    dateRangeSelected: EventEmitter<DateRanges>;

    @Input()
    currentView: FullViewCalendarViewType;

    @Input()
    fullViewCalendarId?: string;

    @Output()
    currentViewChange: EventEmitter<FullViewCalendarViewType>;

    readonly hours: number[];

    eventsForPeriod: EventsForPeriod<T>;

    eventCountPerDay: Record<string, number>;

    datepickerCalendarType: CalendarType;

    today: DateTime;

    selectedDateTime: DateTime;

    startOfPeriod!: DateTime;

    endOfPeriod!: DateTime;

    currentPeriod?: DateTime[];

    isCurrentPeriod: boolean;

    defaultTitle: string;

    selectedDate?: Date;

    retreatPeriodIcon: SafeHtml;

    advancePeriodIcon: SafeHtml;

    constructor(
        private readonly dateTimeService: DateTimeService,
        private readonly iconSanitizer: IconSanitizerService
    ) {
        this.retreatPeriodIcon = this.iconSanitizer.getIcon('goBack');
        this.advancePeriodIcon = this.iconSanitizer.getIcon('goNext');
        this.showViewSwitcher = true;
        this.hours = Array.from({ length: 24 }, (_, i) => i);
        this.selectedDateTime = this.dateTimeService.now();
        this.today = this.dateTimeService.now();
        this.isCurrentPeriod = false;
        this.currentPeriod = [];
        this.eventsForPeriod = {};
        this.eventCountPerDay = {};
        this.defaultTitle = '';
        this.currentView = 'week';
        this.datepickerCalendarType = CalendarType.MONTH;
        this.dateRangeSelected = new EventEmitter<DateRanges>();
        this.currentViewChange = new EventEmitter<FullViewCalendarViewType>();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['events']) {
            this.updatePeriod(this.selectedDateTime);
            this.updateEventsForPeriod();
        }
    }

    setView(view: FullViewCalendarViewType): void {
        if (view === this.currentView) return;
        this.currentView = view;
        this.currentViewChange.emit(view);
        this.emitDateRange();
    }

    retreatPeriod(): void {
        this.selectedDateTime = this.selectedDateTime.minus(this.getPeriodAdjustment());
        this.emitDateRange();
    }

    advancePeriod(): void {
        this.selectedDateTime = this.selectedDateTime.plus(this.getPeriodAdjustment());
        this.emitDateRange();
    }

    onViewMoreClicked(date: DateTime) {
        this.selectedDateTime = date;
        this.setView('day');
    }

    onDatepickerChange(event: any): void {
        if (!event) {
            // Clear button event
            this.selectedDateTime = this.dateTimeService.now();
            this.emitDateRange();
            return;
        }

        this.selectedDateTime = this.dateTimeService.getDateTimeFromString(event);
        this.emitDateRange();
    }

    goToToday(): void {
        if (this.dateTimeService.isToday(this.selectedDateTime)) return;
        this.selectedDateTime = this.dateTimeService.now();
        this.emitDateRange();
    }

    onCalendarTypeDisplayChange(calendarType: CalendarType): void {
        this.datepickerCalendarType = calendarType;
    }

    private getPeriodAdjustment() {
        if (this.currentView === 'day') {
            return { days: 1 };
        } else if (this.currentView === 'week') {
            return { weeks: 1 };
        } else if (this.currentView === 'month') {
            return { months: 1 };
        }
        return { weeks: 1 };
    }

    private updateDefaultTitle(): void {
        this.defaultTitle = this.selectedDateTime.toFormat('MM/dd/yyyy');
    }

    private updatePeriod(date: DateTime): void {
        if (this.currentView === 'day') {
            this.startOfPeriod = date.startOf('day');
            this.endOfPeriod = date.endOf('day');
        } else if (this.currentView === 'week') {
            this.startOfPeriod = this.dateTimeService.getStartOfWeek(date);
            this.endOfPeriod = this.dateTimeService.getEndOfWeek(date);
        } else if (this.currentView === 'month') {
            this.startOfPeriod = this.dateTimeService.getStartOfMonth(date, true);
            this.endOfPeriod = this.dateTimeService.getEndOfMonth(date, true);
        }

        this.currentPeriod = Array.from({ length: this.getPeriodLength() }, (_, i) =>
            this.startOfPeriod.plus({ days: i })
        );

        this.isCurrentPeriod = this.currentPeriod.some((day) => {
            if (this.currentView === 'month') {
                return date.hasSame(this.today, 'month') && day.hasSame(this.today, 'year');
            } else {
                return day.hasSame(this.today, 'day');
            }
        });

        this.selectedDate = this.dateTimeService.formatDateTimeToDate(this.selectedDateTime);

        this.updateDefaultTitle();
    }

    private getPeriodLength(): number {
        if (this.currentView === 'day') {
            return 1;
        } else if (this.currentView === 'week') {
            return 7;
        } else if (this.currentView === 'month') {
            return this.endOfPeriod.diff(this.startOfPeriod, 'days').days + 1;
        } else {
            return 7;
        }
    }

    private updateEventsForPeriod(): void {
        this.eventsForPeriod = {};

        if (this.currentPeriod) {
            for (const day of this.currentPeriod) {
                const dayKey = day.toISODate() || '';
                this.eventCountPerDay[dayKey] = 0;

                for (const hour of this.hours) {
                    const eventsForHour = this.events?.filter((event) => {
                        if (event.date?.hasSame(day, 'day')) {
                            const eventTime = DateTime.fromFormat(event.startTime ?? '', 'HH:mm');
                            return eventTime.hour === hour || (eventTime.hour === hour && eventTime.minute > 0);
                        }
                        return false;
                    });

                    if (!this.eventsForPeriod[dayKey]) {
                        this.eventsForPeriod[dayKey] = {};
                    }

                    this.eventsForPeriod[dayKey][hour] = eventsForHour;

                    if (eventsForHour) this.eventCountPerDay[dayKey] += eventsForHour?.length;
                }
            }
        }
    }

    private emitDateRange(): void {
        let dateFrom: string | null = this.selectedDateTime.toISO();
        let dateTo: string | null = this.selectedDateTime.toISO();

        if (this.currentView === 'day') {
            dateFrom = this.selectedDateTime.startOf('day').toISO();
            dateTo = this.selectedDateTime.endOf('day').toISO();
        } else if (this.currentView === 'week') {
            dateFrom = this.dateTimeService.getStartOfWeek(this.selectedDateTime).toISO();
            dateTo = this.dateTimeService.getEndOfWeek(this.selectedDateTime).toISO();
        } else if (this.currentView === 'month') {
            dateFrom = this.dateTimeService.getStartOfMonth(this.selectedDateTime, true).toISO();
            dateTo = this.dateTimeService.getEndOfMonth(this.selectedDateTime, true).toISO();
        }

        this.dateRangeSelected.emit({ dateFrom, dateTo });
    }
}
